import { create } from 'zustand'

export type ThemeMode = 'light' | 'dark'
export type LightSurface = 'paper' | 'linen' | 'mist'
export type DarkSurface = 'charcoal' | 'midnight' | 'slate'
export type Accent = 'purple' | 'blue' | 'teal' | 'orange' | 'crimson'

export interface SurfaceOption<K extends string> {
  key: K
  label: string
  description: string
  /** Three swatches — ground, panel, raised panel — previewed in the Appearance dialog. */
  swatches: [string, string, string]
}

export type LightSurfaceOption = SurfaceOption<LightSurface>
export type DarkSurfaceOption = SurfaceOption<DarkSurface>

export interface AccentOption {
  key: Accent
  label: string
  /** Swatches per mode, so the disc in the picker is the colour you actually get. */
  swatch: Record<ThemeMode, string>
}

export const LIGHT_SURFACES: LightSurfaceOption[] = [
  {
    key: 'paper',
    label: 'Paper',
    description: 'Current neutral paper',
    swatches: ['#f5f6f8', '#ffffff', '#eef0f4'],
  },
  {
    key: 'linen',
    label: 'Warm linen',
    description: 'Off-white with a warm cast',
    swatches: ['#f6f3ee', '#fffdf9', '#efe9e0'],
  },
  {
    key: 'mist',
    label: 'Cool mist',
    description: 'A cool blue-grey page',
    swatches: ['#eef2f8', '#ffffff', '#e3e9f2'],
  },
]

export const DARK_SURFACES: DarkSurfaceOption[] = [
  {
    key: 'charcoal',
    label: 'Charcoal',
    description: 'Current neutral charcoal',
    swatches: ['#1a1c1f', '#232629', '#2b2e33'],
  },
  {
    key: 'midnight',
    label: 'Midnight blue',
    description: 'Black ground with dark-blue panels',
    swatches: ['#0b0d14', '#141926', '#1b2233'],
  },
  {
    key: 'slate',
    label: 'Blue slate',
    description: 'A softer blue-grey console',
    swatches: ['#1d2330', '#273040', '#2f3a4c'],
  },
]

export const ACCENTS: AccentOption[] = [
  { key: 'purple', label: 'Purple', swatch: { light: '#6b3fd4', dark: '#8f6ae6' } },
  { key: 'blue', label: 'Blue', swatch: { light: '#2f55c8', dark: '#5c8cf0' } },
  { key: 'teal', label: 'Teal', swatch: { light: '#1f7a6b', dark: '#4fb8a3' } },
  { key: 'orange', label: 'Orange', swatch: { light: '#c25a15', dark: '#e5873c' } },
  { key: 'crimson', label: 'Crimson', swatch: { light: '#b32a5b', dark: '#e0608d' } },
]

export interface ThemePreferences {
  mode: ThemeMode
  /** The two surfaces are remembered separately, so switching mode restores each choice. */
  lightSurface: LightSurface
  darkSurface: DarkSurface
  accent: Accent
}

/** Shared with the inline pre-paint script in index.html; changing it must change both. */
export const THEME_STORAGE_KEY = 'blueprint.appearance'

export const DEFAULT_THEME: ThemePreferences = {
  mode: 'light',
  lightSurface: 'paper',
  darkSurface: 'charcoal',
  accent: 'blue',
}

const isMode = (value: unknown): value is ThemeMode => value === 'light' || value === 'dark'
const isLightSurface = (value: unknown): value is LightSurface =>
  LIGHT_SURFACES.some((item) => item.key === value)
const isDarkSurface = (value: unknown): value is DarkSurface => DARK_SURFACES.some((item) => item.key === value)
const isAccent = (value: unknown): value is Accent => ACCENTS.some((item) => item.key === value)

/** Never throws: a corrupt or unreadable store falls back to the defaults, field by field. */
export function readStoredTheme(): ThemePreferences {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (!raw) return DEFAULT_THEME
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_THEME
    const value = parsed as Partial<Record<keyof ThemePreferences, unknown>>
    return {
      mode: isMode(value.mode) ? value.mode : DEFAULT_THEME.mode,
      lightSurface: isLightSurface(value.lightSurface) ? value.lightSurface : DEFAULT_THEME.lightSurface,
      darkSurface: isDarkSurface(value.darkSurface) ? value.darkSurface : DEFAULT_THEME.darkSurface,
      accent: isAccent(value.accent) ? value.accent : DEFAULT_THEME.accent,
    }
  } catch {
    return DEFAULT_THEME
  }
}

function persist(preferences: ThemePreferences): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(preferences))
  } catch {
    // Private browsing or a full quota: the theme still applies for this session.
  }
}

/** Writes the axes the stylesheet selects on. Only the live mode's surface has any effect. */
export function applyTheme(preferences: ThemePreferences): void {
  const root = document.documentElement
  root.dataset.theme = preferences.mode
  root.dataset.lightSurface = preferences.lightSurface
  root.dataset.darkSurface = preferences.darkSurface
  root.dataset.accent = preferences.accent
}

interface ThemeStore extends ThemePreferences {
  setMode: (mode: ThemeMode) => void
  toggleMode: () => void
  setLightSurface: (lightSurface: LightSurface) => void
  setDarkSurface: (darkSurface: DarkSurface) => void
  setAccent: (accent: Accent) => void
}

const initial = typeof window === 'undefined' ? DEFAULT_THEME : readStoredTheme()

export const useThemeStore = create<ThemeStore>((set, get) => {
  const commit = (patch: Partial<ThemePreferences>) => {
    const next: ThemePreferences = {
      mode: patch.mode ?? get().mode,
      lightSurface: patch.lightSurface ?? get().lightSurface,
      darkSurface: patch.darkSurface ?? get().darkSurface,
      accent: patch.accent ?? get().accent,
    }
    set(patch)
    applyTheme(next)
    persist(next)
  }

  return {
    ...initial,
    setMode: (mode) => commit({ mode }),
    toggleMode: () => commit({ mode: get().mode === 'dark' ? 'light' : 'dark' }),
    setLightSurface: (lightSurface) => commit({ lightSurface }),
    setDarkSurface: (darkSurface) => commit({ darkSurface }),
    setAccent: (accent) => commit({ accent }),
  }
})
